import { cellsFor, pieceColorVar, type PieceType, type Rotation } from "@/game/pieces";

interface TetrominoProps {
  readonly type: PieceType;
  readonly rotation?: Rotation;
  /** Edge length of one cell, in pixels. */
  readonly cell?: number;
  /** Hollow pieces are outline-only — used for anything not currently in play. */
  readonly filled?: boolean;
  readonly className?: string;
  readonly title?: string;
}

/**
 * A tetromino drawn as keylined cells.
 *
 * Every coloured surface on this site carries an ink keyline; without it the
 * amber O piece would disappear into the marigold ground.
 */
export function Tetromino({
  type,
  rotation = 0,
  cell = 12,
  filled = true,
  className,
  title,
}: TetrominoProps) {
  const cells = cellsFor(type, rotation);
  const xs = cells.map(([x]) => x);
  const ys = cells.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(...xs) - minX + 1;
  const height = Math.max(...ys) - minY + 1;

  const stroke = 2;
  const half = stroke / 2;

  return (
    <svg
      width={width * cell + stroke}
      height={height * cell + stroke}
      viewBox={`0 0 ${width * cell + stroke} ${height * cell + stroke}`}
      className={className}
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {cells.map(([x, y]) => (
        <rect
          key={`${x}-${y}`}
          x={(x - minX) * cell + half}
          y={(y - minY) * cell + half}
          width={cell}
          height={cell}
          fill={filled ? pieceColorVar(type) : "transparent"}
          stroke="var(--color-ink)"
          strokeWidth={stroke}
          shapeRendering="crispEdges"
        />
      ))}
    </svg>
  );
}
